import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Clock, Calendar, ChevronLeft, ChevronRight, Loader2, CheckCircle2, AlertCircle, Store } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOW_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

function formatDay(d) {
  return `${DOW_FR[d.getDay()]} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Picker de créneau Click & Collect.
 * Props:
 *  - onPick({ location_id, location_name, slot_at }) quand l'utilisateur sélectionne et valide
 *  - onCancel() pour annuler
 *  - maxDaysAhead (optionnel, default 14)
 */
export default function PickupPicker({ onPick, onCancel, maxDaysAhead = 14 }) {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [offset, setOffset] = useState(0); // semaine 0, 1, 2…
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState(null); // null=not loaded, []=empty
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/shop/pickup-locations`);
        setLocations(data || []);
        if ((data || []).length > 0) setLocationId(data[0].id);
      } catch { toast.error("Impossible de charger les points de retrait"); }
    })();
  }, []);

  // Fenêtre de 7 jours à partir de aujourd'hui + offset
  const days = useMemo(() => {
    const arr = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + offset * 7 + i);
      arr.push(d);
    }
    return arr;
  }, [offset]);

  const loadSlots = async (date) => {
    if (!locationId || !date) return;
    setLoading(true);
    setSlots(null);
    try {
      const { data } = await axios.get(`${API}/shop/pickup-slots`, { params: { location_id: locationId, date } });
      setSlots(data.slots || []);
    } catch (e) {
      setSlots([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedDate && locationId) loadSlots(selectedDate);
    setPicked(null);
  // eslint-disable-next-line
  }, [selectedDate, locationId]);

  const currentLocation = locations.find(l => l.id === locationId);

  if (locations.length === 0) {
    return (
      <div className="p-6 text-center" data-testid="pickup-no-locations">
        <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Aucun point de retrait disponible pour le moment.</p>
        <button onClick={onCancel} className="mt-4 text-sm font-semibold text-[color:var(--brand-sky)] hover:underline">Retour</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="pickup-picker">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b">
        <Store className="w-5 h-5 text-[color:var(--brand-sky)]" />
        <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Retrait en magasin</h3>
      </div>

      {/* Location picker */}
      <div className="py-3">
        <label className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-1.5 block flex items-center gap-1"><MapPin className="w-3 h-3" />Point de retrait</label>
        <div className="space-y-1.5">
          {locations.map(l => (
            <button
              key={l.id}
              type="button"
              onClick={() => { setLocationId(l.id); setSelectedDate(""); setSlots(null); setPicked(null); }}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                locationId === l.id ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-navy)]/30"
              }`}
              data-testid={`pickup-location-${l.id}`}
            >
              <div className="font-bold text-sm text-[color:var(--brand-navy)]">{l.name}</div>
              {(l.address || l.city) && (
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  {[l.address, l.postal_code, l.city].filter(Boolean).join(", ")}
                </div>
              )}
              {l.notes && <div className="text-[11px] text-[color:var(--brand-sky)] mt-1 italic">{l.notes}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Day strip */}
      {locationId && (
        <div className="pb-3 border-t pt-3">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-1.5 block flex items-center gap-1 justify-between">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />Date de retrait</span>
            <span className="flex items-center gap-1">
              <button type="button" onClick={() => setOffset(o => Math.max(0, o - 1))} disabled={offset === 0} className="w-6 h-6 rounded border flex items-center justify-center disabled:opacity-30" data-testid="pickup-prev-week"><ChevronLeft className="w-3 h-3" /></button>
              <button type="button" onClick={() => setOffset(o => Math.min(Math.floor(maxDaysAhead / 7), o + 1))} disabled={(offset + 1) * 7 >= maxDaysAhead} className="w-6 h-6 rounded border flex items-center justify-center disabled:opacity-30" data-testid="pickup-next-week"><ChevronRight className="w-3 h-3" /></button>
            </span>
          </label>
          <div className="grid grid-cols-7 gap-1">
            {days.map(d => {
              const iso = isoDate(d);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const isPast = d < today;
              const isSelected = selectedDate === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={isPast}
                  onClick={() => setSelectedDate(iso)}
                  className={`py-2 rounded-lg text-[10px] font-bold transition-all flex flex-col items-center ${
                    isSelected ? "bg-[color:var(--brand-navy)] text-white" :
                    isPast ? "text-[hsl(var(--muted-foreground))]/30 cursor-not-allowed" :
                    "bg-[hsl(var(--muted))]/30 text-[color:var(--brand-navy)] hover:bg-[hsl(var(--muted))]/60"
                  }`}
                  data-testid={`pickup-day-${iso}`}
                >
                  <span className="uppercase">{DOW_FR[d.getDay()]}</span>
                  <span className="text-sm">{d.getDate()}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Slots */}
      {selectedDate && (
        <div className="flex-1 overflow-y-auto pt-3 border-t" data-testid="pickup-slots-grid">
          <label className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2 block flex items-center gap-1"><Clock className="w-3 h-3" />Créneaux disponibles</label>
          {loading ? (
            <div className="flex items-center justify-center py-6 text-xs text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Chargement…</div>
          ) : slots && slots.length === 0 ? (
            <div className="text-center py-6 text-xs text-[hsl(var(--muted-foreground))]">Aucun créneau disponible ce jour-là.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
              {(slots || []).map(slot => (
                <button
                  key={slot.start}
                  type="button"
                  disabled={!slot.available}
                  onClick={() => setPicked({ slot_at: slot.start, label: slot.label })}
                  className={`py-2 rounded-lg text-xs font-bold transition-all relative ${
                    picked?.slot_at === slot.start ? "bg-[color:var(--brand-red)] text-white ring-2 ring-[color:var(--brand-red)]" :
                    !slot.available ? "bg-[hsl(var(--muted))]/10 text-[hsl(var(--muted-foreground))]/30 line-through cursor-not-allowed" :
                    "bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-navy)] hover:bg-[color:var(--brand-sky)]/25 hover:-translate-y-0.5"
                  }`}
                  data-testid={`pickup-slot-${slot.start}`}
                >
                  {slot.label}
                  {slot.capacity > 0 && slot.taken > 0 && slot.available && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 text-white text-[9px] font-black flex items-center justify-center">{slot.capacity - slot.taken}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="pt-3 border-t flex gap-2 sticky bottom-0 bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-11 rounded-full border-2 border-[hsl(var(--border))] text-[color:var(--brand-navy)] font-bold text-sm"
          data-testid="pickup-cancel"
        >Retour</button>
        <button
          type="button"
          disabled={!picked || !currentLocation}
          onClick={() => onPick({ location_id: currentLocation.id, location_name: currentLocation.name, slot_at: picked.slot_at, label: picked.label })}
          className="flex-[2] h-11 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pickup-confirm"
        >
          <CheckCircle2 className="w-4 h-4" />
          {picked ? `Confirmer (${picked.label})` : "Choisir un créneau"}
        </button>
      </div>
    </div>
  );
}
